import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bookmark, Inbox, RefreshCw, Trash2, X } from 'lucide-react';

import {
  bulkTicketAction,
  listTickets,
  CS_TICKET_PRIORITIES,
  CS_TICKET_STATUSES,
  UNASSIGNED_FILTER,
  type BulkTicketAction,
  type CsQueueTicket,
  type CsTicketPriority,
  type CsTicketStatus,
  type CsTier,
  type TicketListFilters,
} from '@/api/cs/tickets.api';
import { listCsAgents } from '@/api/cs/agents.api';
import { listCsGroups, listCsTags, listKwcOrgLinks } from '@/api/cs/routing.api';
import { createSavedView, deleteSavedView, listSavedViews, type CsSavedView } from '@/api/cs/workspace.api';
import { PriorityBadge, StatusBadge, TagChip } from '@/components/cs/badges';
import { PRIORITY_META, STATUS_META } from '@/components/cs/meta';
import { CsAccessDenied, CsErrorCard, CsLoading, SELECT_CLS } from '@/components/cs/PageStates';
import { describeCsError } from '@/components/cs/csError';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';
import { hasCapability } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * CS ticket queue — /cs (Phase 16 / ADR-026, spec §3.1).
 *
 * Filter bar (status/priority multi, assignee incl. Unassigned, group,
 * tags, brand = KwcOrgLink, tier, text search) + per-user saved views +
 * paged table (25/page, last-activity desc) + checkbox multi-select →
 * bulk bar (assign / status / priority / add tags via POST /bulk).
 * Near-real-time via a 30s TanStack refetchInterval + manual refresh —
 * the webhook→DB path keeps the server fresh (ADR-026 deviation from a
 * Chatwoot WebSocket).
 */

const REFETCH_INTERVAL_MS = 30_000;
const SEARCH_DEBOUNCE_MS = 300;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** Compact "how old is this ticket" stamp. */
function formatAge(iso: string, now = Date.now()): string {
  const delta = Math.max(0, now - new Date(iso).getTime());
  if (delta < HOUR_MS) return `${String(Math.floor(delta / 60_000))}m`;
  if (delta < DAY_MS) return `${String(Math.floor(delta / HOUR_MS))}h`;
  return `${String(Math.floor(delta / DAY_MS))}d`;
}

function membershipName(m: { user: { firstName: string; lastName: string } } | null | undefined): string {
  if (!m) return '—';
  return `${m.user.firstName} ${m.user.lastName}`.trim() || '—';
}

export function CsQueuePage() {
  const { state: authState } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const canUseCs = me !== null && hasCapability(me, 'use_cs');

  // ── Filters ───────────────────────────────────────────────────────────
  const [status, setStatus] = useState<CsTicketStatus[]>([]);
  const [priority, setPriority] = useState<CsTicketPriority[]>([]);
  const [assignee, setAssignee] = useState('');
  const [groupId, setGroupId] = useState('');
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [kwcOrgLinkId, setKwcOrgLinkId] = useState('');
  const [tier, setTier] = useState<'' | CsTier>('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
    };
  }, [qInput]);

  const filters: TicketListFilters = useMemo(
    () => ({
      ...(status.length > 0 ? { status } : {}),
      ...(priority.length > 0 ? { priority } : {}),
      ...(assignee !== '' ? { assigneeMembershipId: assignee } : {}),
      ...(groupId !== '' ? { groupId } : {}),
      ...(tagFilters.length > 0 ? { tags: tagFilters } : {}),
      ...(kwcOrgLinkId !== '' ? { kwcOrgLinkId } : {}),
      ...(tier !== '' ? { tier } : {}),
      ...(q !== '' ? { q } : {}),
    }),
    [status, priority, assignee, groupId, tagFilters, kwcOrgLinkId, tier, q],
  );

  // Any filter change restarts pagination.
  useEffect(() => {
    setPage(1);
  }, [filters]);

  function applyFilters(f: TicketListFilters): void {
    setStatus(f.status ?? []);
    setPriority(f.priority ?? []);
    setAssignee(f.assigneeMembershipId ?? '');
    setGroupId(f.groupId ?? '');
    setTagFilters(f.tags ?? []);
    setKwcOrgLinkId(f.kwcOrgLinkId ?? '');
    setTier(f.tier ?? '');
    setQInput(f.q ?? '');
    setQ(f.q ?? '');
  }

  // ── Queries ───────────────────────────────────────────────────────────
  const ticketsQuery = useQuery({
    queryKey: ['cs', 'tickets', filters, page],
    queryFn: () => listTickets(filters, page),
    enabled: canUseCs,
    refetchInterval: REFETCH_INTERVAL_MS,
  });
  const agentsQuery = useQuery({ queryKey: ['cs', 'agents'], queryFn: listCsAgents, enabled: canUseCs });
  const groupsQuery = useQuery({ queryKey: ['cs', 'groups'], queryFn: listCsGroups, enabled: canUseCs });
  const tagsQuery = useQuery({ queryKey: ['cs', 'tags'], queryFn: listCsTags, enabled: canUseCs });
  const orgsQuery = useQuery({ queryKey: ['cs', 'orgs'], queryFn: listKwcOrgLinks, enabled: canUseCs });
  const viewsQuery = useQuery({ queryKey: ['cs', 'views'], queryFn: listSavedViews, enabled: canUseCs });

  // ── Selection + bulk ──────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const rows = ticketsQuery.data?.rows ?? [];

  function toggleSelected(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  function toggleAllOnPage(): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  }

  const bulkMutation = useMutation({
    mutationFn: (action: BulkTicketAction) => bulkTicketAction([...selected], action),
    onSuccess: (results) => {
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        toast.show(`${String(failed.length)} of ${String(results.length)} tickets failed.`, {
          variant: 'warning',
        });
      } else {
        toast.show(`Updated ${String(results.length)} ticket(s).`, { variant: 'success' });
      }
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['cs', 'tickets'] });
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Bulk action failed.'), { variant: 'danger' });
    },
  });

  // ── Saved views ───────────────────────────────────────────────────────
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [activeViewId, setActiveViewId] = useState('');

  const saveViewMutation = useMutation({
    mutationFn: (name: string) => createSavedView(name, filters),
    onSuccess: (view) => {
      setSaveViewOpen(false);
      setActiveViewId(view.id);
      void queryClient.invalidateQueries({ queryKey: ['cs', 'views'] });
      toast.show('View saved.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not save the view.'), { variant: 'danger' });
    },
  });

  const deleteViewMutation = useMutation({
    mutationFn: (id: string) => deleteSavedView(id),
    onSuccess: () => {
      setActiveViewId('');
      void queryClient.invalidateQueries({ queryKey: ['cs', 'views'] });
      toast.show('View deleted.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not delete the view.'), { variant: 'danger' });
    },
  });

  const views = viewsQuery.data ?? [];
  const activeView: CsSavedView | null = views.find((v) => v.id === activeViewId) ?? null;

  if (!canUseCs) {
    return <CsAccessDenied label="The CS workspace needs the use_cs capability." />;
  }

  const total = ticketsQuery.data?.total ?? 0;
  const pageSize = ticketsQuery.data?.pageSize ?? 25;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const activeTags = (tagsQuery.data ?? []).filter((t) => t.active);

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Customer Service</h1>
          <p className="mt-1 text-[14px] text-kw-muted">Ticket queue — auto-refreshes every 30 seconds.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Saved views"
            value={activeViewId}
            onChange={(e) => {
              const id = e.target.value;
              setActiveViewId(id);
              const view = views.find((v) => v.id === id);
              if (view) applyFilters(view.filtersJson);
            }}
            className={SELECT_CLS}
          >
            <option value="">Saved views…</option>
            {views.map((v) => (
              <option key={v.id} value={v.id}>
                {v.ownerMembershipId === null ? `${v.name} (team)` : v.name}
              </option>
            ))}
          </select>
          {activeView && activeView.ownerMembershipId !== null ? (
            <button
              type="button"
              aria-label={`Delete view ${activeView.name}`}
              onClick={() => {
                deleteViewMutation.mutate(activeView.id);
              }}
              className="rounded p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
            >
              <Trash2 className="size-4" />
            </button>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setSaveViewOpen(true);
            }}
          >
            <Bookmark className="size-4" />
            Save view
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void ticketsQuery.refetch();
            }}
            aria-label="Refresh queue"
          >
            <RefreshCw className={cn('size-4', ticketsQuery.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[12px] font-medium uppercase tracking-wide text-kw-muted">Status</span>
          {CS_TICKET_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status.includes(s)}
              onClick={() => {
                setStatus((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
              }}
              className={cn(
                'rounded-kw-full border px-2.5 py-1 text-[12px] font-medium transition-colors',
                status.includes(s)
                  ? 'border-kw-cerulean bg-kw-cerulean-light text-kw-cerulean-dark'
                  : 'border-kw-border bg-white text-kw-muted hover:text-kw-shark',
              )}
            >
              {STATUS_META[s].label}
            </button>
          ))}
          <span className="ml-3 mr-1 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
            Priority
          </span>
          {CS_TICKET_PRIORITIES.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={priority.includes(p)}
              onClick={() => {
                setPriority((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
              }}
              className={cn(
                'rounded-kw-full border px-2.5 py-1 text-[12px] font-medium transition-colors',
                priority.includes(p)
                  ? 'border-kw-cerulean bg-kw-cerulean-light text-kw-cerulean-dark'
                  : 'border-kw-border bg-white text-kw-muted hover:text-kw-shark',
              )}
            >
              {PRIORITY_META[p].label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Assignee filter"
            value={assignee}
            onChange={(e) => {
              setAssignee(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Any assignee</option>
            <option value={UNASSIGNED_FILTER}>Unassigned</option>
            {(agentsQuery.data ?? []).map((a) => (
              <option key={a.id} value={a.membershipId}>
                {a.membership.user.firstName} {a.membership.user.lastName}
              </option>
            ))}
          </select>
          <select
            aria-label="Group filter"
            value={groupId}
            onChange={(e) => {
              setGroupId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Any group</option>
            {(groupsQuery.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Brand filter"
            value={kwcOrgLinkId}
            onChange={(e) => {
              setKwcOrgLinkId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Any brand</option>
            {(orgsQuery.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Tier filter"
            value={tier}
            onChange={(e) => {
              setTier(e.target.value as '' | CsTier);
            }}
            className={SELECT_CLS}
          >
            <option value="">Any tier</option>
            <option value="tier1_auto">Tier 1 · Auto</option>
            <option value="tier2_semi">Tier 2 · Semi</option>
            <option value="tier3_human">Tier 3 · Human</option>
          </select>
          <select
            aria-label="Add tag filter"
            value=""
            onChange={(e) => {
              const tag = e.target.value;
              if (tag && !tagFilters.includes(tag)) setTagFilters((prev) => [...prev, tag]);
            }}
            className={SELECT_CLS}
          >
            <option value="">Filter by tag…</option>
            {activeTags.map((t) => (
              <option key={t.id} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
          <input
            aria-label="Search tickets"
            type="search"
            value={qInput}
            onChange={(e) => {
              setQInput(e.target.value);
            }}
            placeholder="Search subject or email…"
            className="min-w-48 flex-1 rounded-kw-md border border-kw-border bg-white px-3 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </div>

        {tagFilters.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {tagFilters.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-kw-full bg-kw-cerulean-light px-2 py-0.5 text-[11px] font-medium text-kw-cerulean-dark"
              >
                {t}
                <button
                  type="button"
                  aria-label={`Remove tag filter ${t}`}
                  onClick={() => {
                    setTagFilters((prev) => prev.filter((x) => x !== t));
                  }}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </Card>

      {/* ── Bulk bar ───────────────────────────────────────────────── */}
      {selected.size > 0 ? (
        <BulkBar
          count={selected.size}
          isPending={bulkMutation.isPending}
          agents={(agentsQuery.data ?? []).map((a) => ({
            membershipId: a.membershipId,
            name: `${a.membership.user.firstName} ${a.membership.user.lastName}`,
          }))}
          groups={(groupsQuery.data ?? []).map((g) => ({ id: g.id, name: g.name }))}
          tags={activeTags.map((t) => t.name)}
          onApply={(action) => {
            bulkMutation.mutate(action);
          }}
          onClear={() => {
            setSelected(new Set());
          }}
        />
      ) : null}

      {/* ── Table ──────────────────────────────────────────────────── */}
      {ticketsQuery.isLoading ? (
        <CsLoading />
      ) : ticketsQuery.error ? (
        <CsErrorCard title="Couldn't load the queue" error={ticketsQuery.error} fallback="Unknown error" />
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Inbox className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No tickets match</p>
            <p className="mt-1 text-[13px] text-kw-muted">Adjust the filters, or enjoy the quiet.</p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[900px] text-left">
            <thead>
              <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
                <th className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                  />
                </th>
                <th className="px-3 py-2.5 font-medium">Subject</th>
                <th className="px-3 py-2.5 font-medium">Customer</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 font-medium">Priority</th>
                <th className="px-3 py-2.5 font-medium">Assignee</th>
                <th className="px-3 py-2.5 font-medium">Group</th>
                <th className="px-3 py-2.5 font-medium">Tags</th>
                <th className="px-3 py-2.5 font-medium">Age</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kw-border">
              {rows.map((ticket) => (
                <QueueRow
                  key={ticket.id}
                  ticket={ticket}
                  selected={selected.has(ticket.id)}
                  onToggle={() => {
                    toggleSelected(ticket.id);
                  }}
                  onOpen={() => {
                    void navigate(`/cs/tickets/${ticket.id}`);
                  }}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ── Pagination ─────────────────────────────────────────────── */}
      {total > 0 ? (
        <div className="flex items-center justify-between text-[13px] text-kw-muted">
          <span>
            {String(total)} ticket(s) · page {String(page)} of {String(pageCount)}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
              }}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= pageCount}
              onClick={() => {
                setPage((p) => p + 1);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <SaveViewModal
        open={saveViewOpen}
        isPending={saveViewMutation.isPending}
        onClose={() => {
          setSaveViewOpen(false);
        }}
        onSave={(name) => {
          saveViewMutation.mutate(name);
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Row
// ─────────────────────────────────────────────────────────────────────────

function QueueRow({
  ticket,
  selected,
  onToggle,
  onOpen,
}: {
  ticket: CsQueueTicket;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const fullName = [ticket.csCustomer.firstName, ticket.csCustomer.lastName].filter(Boolean).join(' ');
  const customerName = fullName !== '' ? fullName : (ticket.csCustomer.email ?? '—');
  return (
    <tr onClick={onOpen} className="cursor-pointer text-[13px] text-kw-shark hover:bg-kw-bg">
      <td
        className="px-3 py-2.5"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <input
          type="checkbox"
          aria-label={`Select ${ticket.subject}`}
          checked={selected}
          onChange={onToggle}
        />
      </td>
      <td className="max-w-64 px-3 py-2.5">
        <p className="truncate font-medium">{ticket.subject}</p>
        {ticket.customerOrg ? <p className="text-[11px] text-kw-muted">{ticket.customerOrg.name}</p> : null}
      </td>
      <td className="px-3 py-2.5">
        <p className="truncate">{customerName}</p>
        {ticket.csCustomer.email && customerName !== ticket.csCustomer.email ? (
          <p className="truncate text-[11px] text-kw-muted">{ticket.csCustomer.email}</p>
        ) : null}
      </td>
      <td className="px-3 py-2.5">
        <StatusBadge status={ticket.status} />
      </td>
      <td className="px-3 py-2.5">
        <PriorityBadge priority={ticket.priority} />
      </td>
      <td className="px-3 py-2.5">
        {ticket.assignedMembership ? membershipName(ticket.assignedMembership) : <Badge>Unassigned</Badge>}
      </td>
      <td className="px-3 py-2.5">{ticket.group?.name ?? '—'}</td>
      <td className="max-w-40 px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {ticket.tags.slice(0, 3).map((t) => (
            <TagChip key={t} tag={t} />
          ))}
          {ticket.tags.length > 3 ? (
            <span className="text-[11px] text-kw-muted">+{String(ticket.tags.length - 3)}</span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2.5 text-kw-muted">{formatAge(ticket.createdAt)}</td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Bulk bar
// ─────────────────────────────────────────────────────────────────────────

function BulkBar({
  count,
  isPending,
  agents,
  groups,
  tags,
  onApply,
  onClear,
}: {
  count: number;
  isPending: boolean;
  agents: { membershipId: string; name: string }[];
  groups: { id: string; name: string }[];
  tags: string[];
  onApply: (action: BulkTicketAction) => void;
  onClear: () => void;
}) {
  const [assignTo, setAssignTo] = useState('');
  const [groupTo, setGroupTo] = useState('');
  const [statusTo, setStatusTo] = useState<'' | CsTicketStatus>('');
  const [priorityTo, setPriorityTo] = useState<'' | CsTicketPriority>('');
  const [addTags, setAddTags] = useState<string[]>([]);

  return (
    <Card
      className="flex flex-wrap items-center gap-2 border-kw-cerulean/40 bg-kw-cerulean-light/40 p-3"
      data-testid="cs-bulk-bar"
    >
      <span className="text-[13px] font-medium text-kw-shark">{String(count)} selected</span>

      <select
        aria-label="Bulk assign to"
        value={assignTo}
        onChange={(e) => {
          setAssignTo(e.target.value);
        }}
        className={SELECT_CLS}
      >
        <option value="">Assign to…</option>
        <option value={UNASSIGNED_FILTER}>Unassign</option>
        {agents.map((a) => (
          <option key={a.membershipId} value={a.membershipId}>
            {a.name}
          </option>
        ))}
      </select>
      <select
        aria-label="Bulk move to group"
        value={groupTo}
        onChange={(e) => {
          setGroupTo(e.target.value);
        }}
        className={SELECT_CLS}
      >
        <option value="">Group…</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        variant="secondary"
        disabled={(assignTo === '' && groupTo === '') || isPending}
        onClick={() => {
          onApply({
            type: 'assign',
            ...(assignTo !== ''
              ? { assigneeMembershipId: assignTo === UNASSIGNED_FILTER ? null : assignTo }
              : {}),
            ...(groupTo !== '' ? { groupId: groupTo } : {}),
          });
        }}
      >
        Apply assign
      </Button>

      <select
        aria-label="Bulk set status"
        value={statusTo}
        onChange={(e) => {
          const v = e.target.value as '' | CsTicketStatus;
          setStatusTo(v);
          if (v !== '') onApply({ type: 'set_status', status: v });
        }}
        className={SELECT_CLS}
        disabled={isPending}
      >
        <option value="">Set status…</option>
        {CS_TICKET_STATUSES.map((s) => (
          <option key={s} value={s}>
            {STATUS_META[s].label}
          </option>
        ))}
      </select>

      <select
        aria-label="Bulk set priority"
        value={priorityTo}
        onChange={(e) => {
          const v = e.target.value as '' | CsTicketPriority;
          setPriorityTo(v);
          if (v !== '') onApply({ type: 'set_priority', priority: v });
        }}
        className={SELECT_CLS}
        disabled={isPending}
      >
        <option value="">Set priority…</option>
        {CS_TICKET_PRIORITIES.map((p) => (
          <option key={p} value={p}>
            {PRIORITY_META[p].label}
          </option>
        ))}
      </select>

      <select
        aria-label="Bulk add tag"
        value=""
        onChange={(e) => {
          const tag = e.target.value;
          if (tag && !addTags.includes(tag)) setAddTags((prev) => [...prev, tag]);
        }}
        className={SELECT_CLS}
      >
        <option value="">Add tags…</option>
        {tags.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      {addTags.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-1 rounded-kw-full bg-white px-2 py-0.5 text-[11px] font-medium text-kw-shark"
        >
          {t}
          <button
            type="button"
            aria-label={`Remove ${t} from bulk tags`}
            onClick={() => {
              setAddTags((prev) => prev.filter((x) => x !== t));
            }}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      {addTags.length > 0 ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={isPending}
          onClick={() => {
            onApply({ type: 'add_tags', tags: addTags });
            setAddTags([]);
          }}
        >
          Apply tags
        </Button>
      ) : null}

      <button
        type="button"
        onClick={onClear}
        className="ml-auto rounded p-1 text-kw-muted hover:bg-white hover:text-kw-shark"
        aria-label="Clear selection"
      >
        <X className="size-4" />
      </button>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Save-view modal
// ─────────────────────────────────────────────────────────────────────────

function SaveViewModal({
  open,
  isPending,
  onClose,
  onSave,
}: {
  open: boolean;
  isPending: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState('');
  return (
    <Modal open={open} onClose={onClose} title="Save current filters as a view" size="sm">
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (!name.trim()) return;
          onSave(name.trim());
          setName('');
        }}
        className="space-y-4"
      >
        <Input
          label="View name"
          name="cs-view-name"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="e.g. My open urgents"
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isPending} disabled={!name.trim()}>
            Save view
          </Button>
        </div>
      </form>
    </Modal>
  );
}
